import React from 'react';
import { Box, Typography } from '@mui/material';

import { getCompanyLogoPath } from '../../helpers/companyBranding';
import { ReferralWithScope_UserWithoutId, User_WithoutId } from '../../helpers/DataStructures';

interface ReferralConversationCounterpartyHeaderProps {
  referral: ReferralWithScope_UserWithoutId;
  isOfferOwner: boolean;
}

const getUserDisplayName = (user: User_WithoutId) =>
  `${user.givenName} ${user.familyName}`.trim();

const ReferralConversationCounterpartyHeader: React.FC<
  ReferralConversationCounterpartyHeaderProps
> = ({ referral, isOfferOwner }) => {
  const counterpartyUser = isOfferOwner ? referral.user : referral.offer.user;
  const counterpartyPersonName = getUserDisplayName(counterpartyUser);
  const counterpartyLogoPath = getCompanyLogoPath(counterpartyUser.companyName);
  const counterpartyPrimaryLabel = counterpartyLogoPath
    ? counterpartyPersonName
    : counterpartyUser.companyName || counterpartyPersonName;
  const counterpartySecondaryLabel =
    counterpartyLogoPath || counterpartyUser.companyName
      ? counterpartyPersonName !== counterpartyPrimaryLabel
        ? counterpartyPersonName
        : null
      : null;

  return (
    <Box
      data-testid="conversation-counterparty-header"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: 0.75,
        minWidth: 0,
      }}
    >
      {counterpartyLogoPath ? (
        <Box
          component="img"
          src={counterpartyLogoPath}
          alt={`${counterpartyPrimaryLabel} logo`}
          data-testid="conversation-counterparty-logo"
          sx={{
            // REQ-020: if a known logo exists, show that as the primary identity and the person's name underneath.
            width: 'auto',
            height: 40,
            maxWidth: { xs: 112, sm: 140 },
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
      ) : null}
      <Box
        sx={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.15,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.15, overflowWrap: 'anywhere' }}
        >
          {counterpartyPrimaryLabel}
        </Typography>
        {counterpartySecondaryLabel ? (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', lineHeight: 1.2, overflowWrap: 'anywhere' }}
          >
            {counterpartySecondaryLabel}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default ReferralConversationCounterpartyHeader;
